// Producer milestone (cash-flow) hooks.
//
// One row per dated event (invoice due, payout, projected payout). The
// financials route renders these as a cash-flow forecast chart + per-character
// breakdown. Server returns rows ordered by `occurs_on` ASC.

import { useQuery } from '@tanstack/react-query'
import { type ListEnvelope, api, unwrapItems } from '../api'
import type { MilestoneRow } from './producer-types'

export const milestonesKey = (projectId: string) => ['milestones', projectId] as const

export function useMilestones(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? milestonesKey(projectId) : ['milestones', '__none__'],
    enabled: Boolean(projectId),
    queryFn: () =>
      api<ListEnvelope<MilestoneRow>>(`/api/v1/projects/${projectId}/milestones`).then(unwrapItems),
  })
}

/**
 * Aggregate a flat milestone list into per-month buckets. Pure (no hook) so
 * tests can call it directly. Bucket keys are `YYYY-MM`.
 */
export interface MonthBucket {
  /** YYYY-MM. */
  month: string
  invoice_due_usd: number
  paid_actor_usd: number
  projected_actor_usd: number
  platform_fee_usd: number
}

export function bucketByMonth(rows: MilestoneRow[]): MonthBucket[] {
  const buckets = new Map<string, MonthBucket>()
  for (const row of rows) {
    const month = (row.occurs_on ?? '').slice(0, 7)
    if (!month) continue
    let bucket = buckets.get(month)
    if (!bucket) {
      bucket = {
        month,
        invoice_due_usd: 0,
        paid_actor_usd: 0,
        projected_actor_usd: 0,
        platform_fee_usd: 0,
      }
      buckets.set(month, bucket)
    }
    switch (row.kind) {
      case 'invoice_due':
        bucket.invoice_due_usd += row.amount_usd
        break
      case 'payout_actor':
        if (row.state === 'paid') bucket.paid_actor_usd += row.amount_usd
        else bucket.projected_actor_usd += row.amount_usd
        break
      case 'projected_payout':
        bucket.projected_actor_usd += row.amount_usd
        break
      case 'platform_fee':
        bucket.platform_fee_usd += row.amount_usd
        break
    }
  }
  return Array.from(buckets.values()).sort((a, b) => a.month.localeCompare(b.month))
}
